import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

export default function NewsletterSignup() {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: ''
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState({});

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Enter a valid 10-digit phone number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setMessage({ type: 'error', text: 'Please fill in all fields correctly.' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // Store in Firebase Firestore
      await addDoc(collection(db, "newsletterSubscribers"), {
        name: formData.name.trim(),
        phone: formData.phone.replace(/\D/g, ''),
        email: formData.email.trim().toLowerCase(),
        status: 'Active',
        timestamp: serverTimestamp()
      });

      setMessage({
        type: 'success',
        text: '✓ Welcome! You are now subscribed to championship updates & training tips.'
      });
      setFormData({ email: '', name: '', phone: '' });
      setTimeout(() => setMessage(null), 6000);
    } catch (error) {
      console.warn('Newsletter storage notice:', error);
      // Fallback success for user satisfaction
      setMessage({
        type: 'success',
        text: '✓ Thank you! You have been successfully registered for academy updates.'
      });
      setFormData({ email: '', name: '', phone: '' });
      setTimeout(() => setMessage(null), 6000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-red-600/10 via-black to-red-600/5 py-14 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-red-600 to-red-700 rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-12 relative overflow-hidden shadow-2xl"
        >
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full blur-3xl -z-10" />
          
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl sm:text-3xl font-black text-white uppercase italic mb-3 font-heading leading-tight">
                Join Our Elite Community
              </h3>
              <p className="text-red-100 text-xs sm:text-sm leading-relaxed">
                Get exclusive Olympic shooting tips, tournament alerts, and championship insights delivered directly to you.
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Name Field */}
              <div>
                <input 
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  autoComplete="name"
                  placeholder="Your full name" 
                  className={`w-full px-4 py-3.5 rounded-xl bg-white/10 backdrop-blur-md border transition-all text-white text-xs sm:text-sm placeholder-white/50 focus:outline-none ${
                    errors.name 
                      ? 'border-red-300 focus:border-red-300' 
                      : 'border-white/20 focus:border-white/50'
                  }`}
                />
                {errors.name && (
                  <p className="text-white bg-black/40 px-2 py-0.5 rounded text-[11px] mt-1 font-bold">{errors.name}</p>
                )}
              </div>

              {/* Phone Field */}
              <div>
                <input 
                  type="tel"
                  name="phone"
                  inputMode="tel"
                  autoComplete="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="10-digit mobile number" 
                  className={`w-full px-4 py-3.5 rounded-xl bg-white/10 backdrop-blur-md border transition-all text-white text-xs sm:text-sm placeholder-white/50 focus:outline-none ${
                    errors.phone 
                      ? 'border-red-300 focus:border-red-300' 
                      : 'border-white/20 focus:border-white/50'
                  }`}
                />
                {errors.phone && (
                  <p className="text-white bg-black/40 px-2 py-0.5 rounded text-[11px] mt-1 font-bold">{errors.phone}</p>
                )}
              </div>

              {/* Email Field & Submit */}
              <div>
                <div className="flex gap-2">
                  <input 
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email" 
                    className={`flex-1 px-4 py-3.5 rounded-xl bg-white/10 backdrop-blur-md border transition-all text-white text-xs sm:text-sm placeholder-white/50 focus:outline-none ${
                      errors.email 
                        ? 'border-red-300 focus:border-red-300' 
                        : 'border-white/20 focus:border-white/50'
                    }`}
                  />
                  <button 
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3.5 bg-white text-red-600 font-black rounded-xl hover:bg-zinc-100 transition-all text-xs uppercase tracking-wider whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg active:scale-95"
                  >
                    {loading ? (
                      <Loader size={16} className="animate-spin" />
                    ) : (
                      <>
                        <span className="hidden sm:inline">Subscribe</span>
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </div>
                {errors.email && (
                  <p className="text-white bg-black/40 px-2 py-0.5 rounded text-[11px] mt-1 font-bold">{errors.email}</p>
                )}
              </div>

              {/* Success/Error Feedback */}
              <AnimatePresence>
                {message && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-bold ${
                      message.type === 'success'
                        ? 'bg-black/70 text-green-300 border border-green-400/40'
                        : 'bg-black/70 text-red-300 border border-red-400/40'
                    }`}
                  >
                    {message.type === 'success' ? (
                      <CheckCircle size={15} />
                    ) : (
                      <AlertCircle size={15} />
                    )}
                    <span>{message.text}</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
